import { FC } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { Flex } from '@bookjane2/bookjane-design-library';
import { useCustomerLocationsModalUIContext } from './CustomerLocationsModal.ui.context';
import {
  CustomerLocationsModalHeader,
  CustomerLocationsModalTitle,
  CustomerLocationsModalSubtitle,
  LocationSection,
  LocationTitle,
  LocationDetailsTitle,
  LocationDetails,
  LocationDetailsRow,
  LocationDetailsColumn,
  LocationDetailRow,
  LocationDetailLabel,
  LocationDetailValue,
  ContactInformationTitle,
  ContactInformationContainer,
  ContactInformationRow,
  ContactInformationColumn,
  LocationDivider,
  EditButtonWrapper,
  EditButton,
} from './CustomerLocationsModal.styled';
import { useHistory, useParams } from 'react-router-dom';
import { useCustomersSettingsPageDataContext } from 'pages/Settings/pages/LocationSettings/CustomersSettingsPage/CustomersSettingsPage.data.context';

const CustomerLocationsViewComponent: FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const { customerId } = useCustomerLocationsModalUIContext();
  const { data } = useCustomersSettingsPageDataContext();
  const { t } = useTranslation();
  const history = useHistory();

  const customer = data?.fetchCommunity?.subFacilities?.find(
    (c) => c.displayId === parseInt(customerId, 10),
  );
  const locations = customer?.subFacilityAddresses || [];

  const handleEdit = () => {
    history.push(`/settings/locations/${locationId}/customers/${customerId}/edit`);
  };

  if (!customer) {
    return (
      <Flex justifyContent="center" alignItems="center" height="200px">
        <span>{t('common:CUSTOMER_NOT_FOUND')}</span>
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" width="100%" height="100%">
      <CustomerLocationsModalHeader>
        <CustomerLocationsModalTitle>{customer.name}</CustomerLocationsModalTitle>
        <CustomerLocationsModalSubtitle>
          {t('location_settings:CUSTOMER_LOCATIONS')}
        </CustomerLocationsModalSubtitle>
      </CustomerLocationsModalHeader>

      <Flex flexDirection="column" flex="1">
        {locations.map((location, index: number) => (
          <Flex key={location.displayId} flexDirection="column">
            <LocationSection>
              <LocationTitle>
                {t('location_settings:LOCATION')} {index + 1}
              </LocationTitle>

              <LocationDetailsTitle>{t('location_settings:LOCATION_DETAILS')}</LocationDetailsTitle>

              <LocationDetails>
                <LocationDetailsRow>
                  <LocationDetailsColumn>
                    <LocationDetailRow>
                      <LocationDetailLabel>
                        {t('location_settings:LOCATION_NAME')}
                      </LocationDetailLabel>
                      <LocationDetailValue>{location.name}</LocationDetailValue>
                    </LocationDetailRow>
                  </LocationDetailsColumn>
                  <LocationDetailsColumn>
                    <LocationDetailRow>
                      <LocationDetailLabel>{t('location_settings:ADDRESS')}</LocationDetailLabel>
                      <LocationDetailValue>
                        {location.address}
                        {location.unitNo && `, ${t('location_settings:UNIT')} #${location.unitNo}`}
                      </LocationDetailValue>
                    </LocationDetailRow>
                  </LocationDetailsColumn>
                </LocationDetailsRow>
              </LocationDetails>

              <ContactInformationTitle>
                {t('location_settings:CONTACT_INFORMATION')}
              </ContactInformationTitle>

              <ContactInformationContainer>
                <ContactInformationRow>
                  <ContactInformationColumn>
                    <LocationDetailRow>
                      <LocationDetailLabel>{t('location_settings:NAME')}</LocationDetailLabel>
                      <LocationDetailValue>{location.contactName}</LocationDetailValue>
                    </LocationDetailRow>
                  </ContactInformationColumn>
                </ContactInformationRow>
                <ContactInformationRow>
                  <ContactInformationColumn>
                    <LocationDetailRow>
                      <LocationDetailLabel>{t('location_settings:EMAIL')}</LocationDetailLabel>
                      <LocationDetailValue>{location.contactEmail}</LocationDetailValue>
                    </LocationDetailRow>
                  </ContactInformationColumn>
                  <ContactInformationColumn>
                    <LocationDetailRow>
                      <LocationDetailLabel>
                        {t('location_settings:PHONE_NUMBER')}
                      </LocationDetailLabel>
                      <LocationDetailValue>{location.contactPhone}</LocationDetailValue>
                    </LocationDetailRow>
                  </ContactInformationColumn>
                </ContactInformationRow>
              </ContactInformationContainer>
            </LocationSection>

            {index < locations.length - 1 && <LocationDivider />}
          </Flex>
        ))}
      </Flex>

      <EditButtonWrapper>
        <EditButton onClick={handleEdit}>{t('location_settings:EDIT')}</EditButton>
      </EditButtonWrapper>
    </Flex>
  );
};

export const CustomerLocationsView: FC = () => {
  return <CustomerLocationsViewComponent />;
};
